import { useMemo, useState, type ReactNode } from 'react';
import { ChevronDown, ChevronLeft, ChevronRight, Download, ExternalLink, Loader2, Trophy } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { formatCompact } from '@/lib/format';
import {
  approvedTopEarningClips,
  isMediaDownloadPending,
  type TopEarningClipRow,
} from '@/lib/campaigns/topEarningClips';
import type { SubmissionMediaDownloadState } from '@/hooks/use-cpm-submission-media-download';

export type TopEarningClip = TopEarningClipRow;

/**
 * One page of the campaign's earning ranking. `total`/`page`/`pageCount` come
 * from the paged backend query; without them the card shows `clips` as a
 * single unpaged list.
 */
export function TopEarningClipsCard({
  clips,
  formatEarnings,
  downloadState = {},
  onDownloadClip,
  total,
  page = 1,
  pageSize,
  pageCount = 1,
  onPageChange,
  headerActions,
  loading = false,
  loadError = false,
  onRetry,
}: {
  clips: TopEarningClip[];
  formatEarnings: (cents: number) => string;
  downloadState?: Record<string, SubmissionMediaDownloadState>;
  onDownloadClip?: (clip: TopEarningClip) => void;
  total?: number;
  page?: number;
  pageSize?: number;
  pageCount?: number;
  onPageChange?: (page: number) => void;
  headerActions?: ReactNode;
  loading?: boolean;
  loadError?: boolean;
  onRetry?: () => void;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const ranked = useMemo(() => approvedTopEarningClips(clips), [clips]);
  if (!ranked.length && !loading && !loadError) return null;
  const rankOffset = pageSize ? (page - 1) * pageSize : 0;
  const showPager = !!onPageChange && pageCount > 1;

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <Card data-testid="top-earning-clips">
        <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-3 space-y-0">
          <CollapsibleTrigger asChild>
            <button
              type="button"
              className="flex min-w-0 flex-1 items-center gap-2 text-left"
            >
              <Trophy className="h-4 w-4 shrink-0 text-primary" aria-hidden />
              <CardTitle className="text-base">
                {t('admin.campaigns.submissions.topEarning.title')}
              </CardTitle>
              <span className="text-sm text-muted-foreground">
                {t('admin.campaigns.submissions.topEarning.count', { count: total ?? ranked.length })}
              </span>
              <ChevronDown
                className={`ml-1 h-4 w-4 shrink-0 text-muted-foreground transition-transform ${open ? 'rotate-180' : ''}`}
                aria-hidden
              />
            </button>
          </CollapsibleTrigger>
          {loading && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" aria-hidden />}
          {headerActions}
        </CardHeader>
        {loadError && <div role="alert" className="flex flex-wrap items-center gap-3 px-6 pb-4 text-sm text-destructive">
          {t('admin.campaigns.submissions.topEarning.loadFailed')}
          {onRetry && <Button type="button" variant="outline" size="sm" onClick={onRetry}>{t('common.retry')}</Button>}
        </div>}
        <CollapsibleContent>
          <CardContent>
            <div className="rounded-md border" aria-busy={loading}>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12">
                      {t('admin.campaigns.submissions.topEarning.rank')}
                    </TableHead>
                    <TableHead>{t('admin.campaigns.submissions.table.platform')}</TableHead>
                    <TableHead>{t('admin.campaigns.submissions.table.url')}</TableHead>
                    <TableHead>{t('admin.campaigns.submissions.table.submitter')}</TableHead>
                    <TableHead>{t('admin.campaigns.submissions.table.views')}</TableHead>
                    <TableHead>{t('admin.campaigns.submissions.table.earned')}</TableHead>
                    {onDownloadClip && (
                      <TableHead className="w-28">
                        {t('admin.campaigns.submissions.topEarning.downloadClip')}
                      </TableHead>
                    )}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {ranked.map((clip, index) => {
                    const views = Number.parseInt(clip.views || '0', 10) || 0;
                    const submitter = clip.user_name || clip.user_email || '—';
                    const state = downloadState[clip.id];
                    const pending = isMediaDownloadPending(state?.status);
                    return (
                      <TableRow key={clip.id}>
                        <TableCell className="font-semibold tabular-nums">
                          {rankOffset + index + 1}
                        </TableCell>
                        <TableCell>
                          <span className="rounded bg-primary/10 px-2 py-1 text-xs font-semibold uppercase text-primary">
                            {clip.platform}
                          </span>
                        </TableCell>
                        <TableCell className="max-w-xs">
                          <a
                            href={clip.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex w-full min-w-0 items-center gap-1 text-sm text-blue-400 hover:underline"
                          >
                            <ExternalLink className="h-3 w-3 shrink-0" />
                            <span className="truncate">{clip.url}</span>
                          </a>
                        </TableCell>
                        <TableCell className="text-sm">{submitter}</TableCell>
                        <TableCell className="tabular-nums">{formatCompact(views)}</TableCell>
                        <TableCell className="font-medium tabular-nums">
                          {formatEarnings(clip.earnings_cents)}
                        </TableCell>
                        {onDownloadClip && (
                          <TableCell>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              disabled={pending}
                              onClick={() => onDownloadClip(clip)}
                            >
                              {pending ? (
                                <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                              ) : (
                                <Download className="mr-1 h-4 w-4" />
                              )}
                              {pending
                                ? t('admin.campaigns.submissions.topEarning.downloading')
                                : t('admin.campaigns.submissions.topEarning.downloadClip')}
                            </Button>
                            {state?.status === 'failed' && (
                              <p className="mt-1 text-xs text-destructive">
                                {t('admin.campaigns.submissions.topEarning.downloadFailed')}
                              </p>
                            )}
                            {state?.status === 'unsupported' && (
                              <p className="mt-1 text-xs text-muted-foreground">
                                {t('admin.campaigns.submissions.topEarning.downloadUnsupported')}
                              </p>
                            )}
                          </TableCell>
                        )}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
            {showPager && (
              <div className="mt-4 flex items-center justify-between">
                <span className="text-sm text-muted-foreground">
                  {t('admin.campaigns.submissions.topEarning.pageOf', { page, pageCount })}
                </span>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    aria-label={t('admin.campaigns.submissions.topEarning.previousPage')}
                    onClick={() => onPageChange(Math.max(1, page - 1))}
                    disabled={loading || loadError || page <= 1}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    aria-label={t('admin.campaigns.submissions.topEarning.nextPage')}
                    onClick={() => onPageChange(Math.min(pageCount, page + 1))}
                    disabled={loading || loadError || page >= pageCount}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  );
}
